import React, { useState } from 'react'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useSelector } from 'react-redux'
import { State } from '../types/state'
import { Fab } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteGroupDialog from './DeleteGroupDialog'
import { useIsLargeView, useIsMobileView } from '../hooks/useIsMobileView'
import NewOrEditGroupDialog from './NewOrEditGroupDialog'
import { Role } from '../types/user'

interface GroupsProps {
  caregiverRoles: Role[]
}

const Groups = ({ caregiverRoles }: GroupsProps): JSX.Element => {
  const { groups } = useSelector((state: State) => state.user)
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([])
  const [selectedRowIdsToDelete, setSelectedRowIdsToDelete] = useState<
    number[]
  >([])
  const [group, setGroup] = useState<any | null>(null)
  const [isNewOrEditGroupDialogOpen, setIsNewOrEditGroupDialogOpen] =
    useState(false)
  const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = useState(false)
  const isMobileView = useIsMobileView()
  const isLargeView = useIsLargeView()

  const Caregiver: React.FC<{ id: number }> = ({ id }) => {
    const group = groups.find(g => g.id === id)!

    return (
      <div>{`${group.caregiver.firstName} ${group.caregiver.lastName}`}</div>
    )
  }

  const StudentsList: React.FC<{ id: number }> = ({ id }) => {
    const group = groups.find(g => g.id === id)!
    const studentNames = group.students.map(
      s => `${s.user.firstName} ${s.user.lastName}`
    )

    return <div>{studentNames.join(', ')}</div>
  }

  const OptionsButton: React.FC<{ id: number }> = ({ id }) => {
    return (
      <div>
        <Fab
          size='small'
          onClick={() => {
            setGroup(groups.find(g => g.id === id) as any)
            setIsNewOrEditGroupDialogOpen(true)
          }}
          color='warning'
          aria-label='edit'
          style={{ transform: 'scale(0.8)' }}
        >
          <EditIcon />
        </Fab>
        <Fab
          size='small'
          onClick={() => {
            setSelectedRowIdsToDelete([id])
            setIsDeleteGroupDialogOpen(true)
          }}
          color='error'
          aria-label='delete'
          style={{ transform: 'scale(0.8)' }}
        >
          <DeleteIcon />
        </Fab>
      </div>
    )
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 120 },
    {
      field: 'caregiver',
      headerName: 'Teacher/Tutor/Form Tutor',
      width: 180,
      renderCell: params => <Caregiver id={params.row.id} />
    },
    {
      field: 'students',
      headerName: 'Students',
      width: 400,
      renderCell: params => <StudentsList id={params.row.id} />
    },
    {
      field: 'options',
      headerName: '',
      width: 100,
      renderCell: params => <OptionsButton id={params.row.id} />
    }
  ]

  return (
    <div
      style={{
        height: isMobileView ? '75vh' : '60vh',
        width: isMobileView ? '80vw' : isLargeView ? '50vw' : '70vw',
        margin: 'auto',
        marginBottom: '5vw'
      }}
    >
      <Fab
        size='small'
        color='primary'
        aria-label='add'
        onClick={() => {
          setGroup(null)
          setIsNewOrEditGroupDialogOpen(true)
        }}
        style={{ margin: '1vw' }}
      >
        <AddIcon />
      </Fab>
      <Fab
        size='small'
        onClick={() => {
          setSelectedRowIdsToDelete(selectedRowIds)
          setIsDeleteGroupDialogOpen(true)
        }}
        color='error'
        aria-label='delete'
        disabled={selectedRowIds.length === 0}
      >
        <DeleteIcon />
      </Fab>
      <DataGrid
        disableRowSelectionOnClick
        rows={groups.filter(g =>
          caregiverRoles.some(c => c === g.caregiver.role)
        )}
        columns={columns}
        checkboxSelection
        onRowSelectionModelChange={newSelection => {
          setSelectedRowIds(newSelection as number[])
        }}
      />
      {isNewOrEditGroupDialogOpen && (
        <NewOrEditGroupDialog
          handleClose={() => setIsNewOrEditGroupDialogOpen(false)}
          group={group}
          caregiverRoles={caregiverRoles}
        />
      )}
      {isDeleteGroupDialogOpen && (
        <DeleteGroupDialog
          handleClose={() => setIsDeleteGroupDialogOpen(false)}
          ids={selectedRowIdsToDelete}
        />
      )}
    </div>
  )
}

export default Groups
